import React, { useEffect, useState } from 'react';
import Styles from './Stimpage.module.css';
import {
  FollicularStudyResultsList,
  getFSResults,
} from './helper.stim';
import {
  Chart as ChartJS,
  LinearScale,
  PointElement,
  Tooltip,
  Legend,
  ChartData,
  TooltipItem,
  Title,
  ChartOptions,
} from 'chart.js';
import { Scatter } from 'react-chartjs-2';
import { PaginatedResponse } from '../../types';
import { Typography } from '@mui/material';

ChartJS.register(
  LinearScale,
  PointElement,
  Tooltip,
  Legend,
  Title
);

export interface Follicular {
  patientId: number;
  stim: number;
}

const FollicularGraph: React.FC<Follicular> = ({ patientId, stim }) => {
  const [follicular, setFollicular] = useState<FollicularStudyResultsList[]>([]);
  const [labels, setLabels] = useState<string[]>([]);

  useEffect(() => {
    if(!stim) return;
    getFSResults(stim)
      .then((res: PaginatedResponse<FollicularStudyResultsList[]>) => res.objects)
      .then((results) => {
        setFollicular(results);
        if (results.length) {
          const labelSet = Array.from(new Set(
            results.map((result) => {
              const resultDate = new Date(result.date);
              const formatted = resultDate.toLocaleDateString('en-GB', { day: 'numeric', month: 'short' });
              return `${result.cycle_day} (${formatted})`;
            })
          )).sort((a, b) => {
            const getDay = (str: string) => Number(str.split(' ')[0]);
            return getDay(a) - getDay(b);
          });
          setLabels(labelSet);
        }
      });
  }, [patientId, stim]);

  const labelIndexMap = labels.reduce((acc, label, idx) => {
    acc[label] = idx;
    return acc;
  }, {} as Record<string, number>);

  const indexToLabel = labels;

  const jitteredX = (label: string) => {
    const base = labelIndexMap[label] ?? 0;
    const jitter = (Math.random() - 0.5) * 0.6; // Jitter within ±0.3
    return base + jitter;
  };

  const scatterData: ChartData<'scatter', { x: number; y: number; side: string; count: number | null }[], number> = {
    datasets: [
      {
        label: 'Left Ovary',
        data: follicular.flatMap(entry =>
          (entry.details ?? [])
            .filter(d => d.left_follicle_size)
            .flatMap(d => {
              const count = parseInt(d.left_no_of_follicles ?? '1') || 1;
              const resultDate = new Date(entry.date);
              const dateLabel = `${entry.cycle_day} (${resultDate.toLocaleDateString('en-GB', { day: 'numeric', month: 'short' })})`;
              return Array.from({ length: count }).map(() => ({
                x: jitteredX(dateLabel),
                y: parseFloat(d.left_follicle_size ?? '0'),
                side: 'Left',
                count: count,
                cycle_day: entry.cycle_day
              }));
            })
        ),
        backgroundColor: 'rgba(128, 128, 128, 0.3)',
        pointRadius: 6,
        pointHoverRadius: 4,
      },
      {
        label: 'Right Ovary',
        data: follicular.flatMap(entry =>
          (entry.details ?? [])
            .filter(d => d.right_follicle_size)
            .flatMap(d => {
              const count = parseInt(d.right_no_of_follicles ?? '1') || 1;
              const resultDate = new Date(entry.date);
              const dateLabel = `${entry.cycle_day} (${resultDate.toLocaleDateString('en-GB', { day: 'numeric', month: 'short' })})`;
              return Array.from({ length: count }).map(() => ({
                x: jitteredX(dateLabel),
                y: parseFloat(d.right_follicle_size ?? '0'),
                side: 'Right',
                count: count,
                cycle_day: entry.cycle_day
              }));
            })
        ),
        backgroundColor: '#2e2e3a',
        pointRadius: 6,
        pointHoverRadius: 8,
      }
    ]
  };

  const options: ChartOptions<'scatter'> = {
    responsive: true,
    plugins: {
      tooltip: {
        callbacks: {
          title: (items: TooltipItem<'scatter'>[]) => {
            // Only one title — use first item
            return `${(items[0].raw as { side: string }).side}`;
          },
          afterLabel: (ctx) => {
            const dataset = ctx.chart.data.datasets[ctx.datasetIndex].data;
            const rawPoint = ctx.raw as { x: string; y: number; count: number | null };
            const matching = (dataset as { x: number; y: number; count: number | null }[]).filter(
              (d: { x: number; y: number; count: number | null }) => 
                d.x === Number(rawPoint.x) && 
                d.y === rawPoint.y && 
                d.count === rawPoint.count
            );
          
            if (ctx.dataIndex === (dataset as { x: number; y: number; count: number | null }[]).indexOf(matching[0])) {
              return [
                `Size : ${rawPoint.y} mm`,        
                `Day : ${(ctx.dataset.data?.[ctx.dataIndex] as { cycle_day?: string })?.cycle_day ?? ''}`,
                `#Follicles : ${rawPoint.count?.toString().padStart(2, '0')}`
              ];
            }
          
            return '';
          }
          
        },
        backgroundColor: '#fff',
        borderColor: '#F28C7E',
        borderWidth: 1,
        titleColor: '#F28C7E',
        titleFont: { weight: 'bold' },
        bodyColor: '#333',
        bodyFont: { size: 13 },
        padding: 12,
        displayColors: false
      },
      legend: {
        position: 'top'
      }
    },
    scales: {
      x: {
        type: 'linear',
        title: {
          display: true,
          text: 'Cycle Day',
          
        },
        min: -0.5,
        max: labels.length - 0.5,
        ticks: {
          stepSize: 1,
          callback: function (val: string | number) {
            return indexToLabel[Math.round(Number(val))] ?? '';
          },
          padding: 10,
          align: 'start',
        },
        grid: {
          drawTicks: false
        }
      },
      y: {
        title: {
          display: true,
          text: 'Size (mm)'
        },
        beginAtZero: true,
        min: 0,
        max: 30,
        ticks: {
          stepSize: 2
        }
      }
    }
    
  };

  return (
    <div className={Styles.hormonal}>
      {follicular.length > 0 ? <Scatter data={scatterData} options={options} style={{ width: '100%', height: '100%' }} /> : <Typography>No data available</Typography>}
    </div>
  );
};

export default FollicularGraph;

// import React, { useCallback, useEffect, useState } from 'react';
// import Styles from './Stimpage.module.css';
// import {
//   FollicularStudyResultsList,
//   getFSResults,
//   getStim,
//   Stim,
// } from './helper.stim';
// import { convertToLocalDateTime } from '../../services/time';
// import {
//   Chart as ChartJS,
//   LinearScale,
//   PointElement,
//   Tooltip,
//   Legend,
//   ChartData,
//   TooltipItem,
//   Title,
//   ChartOptions,
// } from 'chart.js';
// import { Scatter } from 'react-chartjs-2';
// import { PaginatedResponse } from '../../types';
// import { Chart } from 'chart.js/dist';

// ChartJS.register(
//   LinearScale,
//   PointElement,
//   Tooltip,
//   Legend,
//   Title
// );

// export interface Follicular {
//   patientId: number;
//   stim: number;
// }

// const FollicularGraph: React.FC<Follicular> = ({ patientId, stim }) => {
//   const [firstDate, setFirstDate] = useState<Date | null>(null);
//   const [follicular, setFollicular] = useState<FollicularStudyResultsList[]>([]);
//   const [labels, setLabels] = useState<string[]>([]);

//   const daysFromFirstDate = useCallback((date: Date) => {
//     if (firstDate) {
//       return Math.ceil((date.getTime() - firstDate.getTime()) / (1000 * 60 * 60 * 24)) + 1;
//     }
//     return Math.ceil((date.getTime() - new Date().getTime()) / (1000 * 60 * 60 * 24)) + 1;
//   }, [firstDate]);

//   useEffect(() => {
//     getFSResults(stim)
//       .then((res: PaginatedResponse<FollicularStudyResultsList[]>) => res.objects)
//       .then((results) => {
//         setFollicular(results);
//         if (results.length) {
//           const first = results[0];
//           setFirstDate(new Date(first.date));
//         }
//       });
//   }, [patientId, stim]);

//   useEffect(() => {
//     const labelSet = Array.from(new Set(
//       follicular.map((result) => daysFromFirstDate(new Date(result.date)).toString())
//     )).sort((a, b) => Number(a) - Number(b));
//     setLabels(labelSet);
//   }, [follicular, daysFromFirstDate]);

//   const scatterData: ChartData<'scatter', { x: string; y: number; side: string; count: number | null }[], string> = {
//     // datasets: [
//     //   {
//     //     label: 'Left Ovary',
//     //     data: follicular.flatMap(entry =>
//     //       (entry.details?.filter(d => d.left_follicle_size)
//     //         .map(d => ({
//     //           x: `${entry.cycle_day} (${new Date(entry.date).toLocaleDateString('en-GB', { day: 'numeric', month: 'short' })})`,
//     //           y: parseFloat(d.left_follicle_size ?? '0'),
//     //           side: 'Left',
//     //           count: parseInt(d.left_no_of_follicles ?? '0') || null,
//     //         })) ?? [])
//     //     ).filter((d): d is { x: string; y: number; side: string; count: number | null } => d !== undefined),
//     //     backgroundColor: 'rgba(128, 128, 128, 0.3)',
//     //     pointRadius: 6,
//     //     pointHoverRadius: 8,
//     //   },
//     //   {
//     //     label: 'Right Ovary',
//     //     // data: follicular.flatMap(entry =>
//     //     //   entry.details?.filter(d => d.right_follicle_size)
//     //     //     .map(d => ({
//     //     //       x: `${entry.cycle_day} (${new Date(entry.date).toLocaleDateString('en-GB', { day: 'numeric', month: 'short' })})`,
//     //     //       y: parseFloat(d.right_follicle_size ?? '0'),
//     //     //       side: 'Right',
//     //     //       count: parseInt(d.right_no_of_follicles ?? '0') || null,
//     //     //     }))
//     //     // ),
//     //     data: follicular.flatMap(entry =>
//     //       (entry.details?.filter(d => d.right_follicle_size)
//     //         .map(d => ({
//     //           x: `${entry.cycle_day} (${new Date(entry.date).toLocaleDateString('en-GB', { day: 'numeric', month: 'short' })})`,
//     //           y: parseFloat(d.right_follicle_size ?? '0'),
//     //           side: 'Right',
//     //           count: parseInt(d.right_no_of_follicles ?? '0') || null,
//     //         })) ?? [])
//     //     ).filter((d): d is { x: string; y: number; side: string; count: number | null } => d !== undefined),
//     //     backgroundColor: '#2e2e3a',
//     //     pointRadius: 6,
//     //     pointHoverRadius: 8,
//     //   }
//     // ]
//     datasets: [
//       {
//         label: 'Left Ovary',
//         data: follicular.flatMap(entry =>
//           (entry.details ?? [])
//             .filter(d => d.left_follicle_size)
//             .flatMap(d => {
//               const count = parseInt(d.left_no_of_follicles ?? '1') || 1;
//               return Array.from({ length: count }).map(() => ({
//                 x: `${entry.cycle_day} (${new Date(entry.date).toLocaleDateString('en-GB', { day: 'numeric', month: 'short' })})`,
//                 y: parseFloat(d.left_follicle_size ?? '0'),
//                 side: 'Left',
//                 count: count
//               }));
//             })
//         ),
//         backgroundColor: 'rgba(128, 128, 128, 0.3)',
//         pointRadius: 6,
//         pointHoverRadius: 8,
//       },
//       {
//         label: 'Right Ovary',
//         data: follicular.flatMap(entry =>
//           (entry.details ?? [])
//             .filter(d => d.right_follicle_size)
//             .flatMap(d => {
//               const count = parseInt(d.right_no_of_follicles ?? '1') || 1;
//               return Array.from({ length: count }).map(() => ({
//                 x: `${entry.cycle_day} (${new Date(entry.date).toLocaleDateString('en-GB', { day: 'numeric', month: 'short' })})`,
//                 y: parseFloat(d.right_follicle_size ?? '0'),
//                 side: 'Right',
//                 count: count
//               }));
//             })
//         ),
//         backgroundColor: '#2e2e3a',
//         pointRadius: 6,
//         pointHoverRadius: 8,
//       }
//     ]
//   };

//   const options : ChartOptions<'scatter'> = {
//     responsive: true,
//     plugins: {
//       tooltip: {
//         callbacks: {
//           title: (items: TooltipItem<'scatter'>[]) => `${(items[0].raw as { side: string }).side}`,
//           label: (ctx: unknown) => {
//             const { y, x, count } = (ctx as TooltipItem<'scatter'>).raw as { y: number; x: string; count: number | null };
//             return [
//               `Size : ${y} mm`,
//               `Day : ${x}`,
//               `#Follicles : ${count?.toString().padStart(2, '0')}`
//             ];
//           },
//         },
//         backgroundColor: '#fff',
//         borderColor: '#F28C7E',
//         borderWidth: 1,
//         titleColor: '#F28C7E',
//         titleFont: { weight: 'bold' as const, size: 15 },
//         bodyColor: '#333',
//         bodyFont: { size: 13 },
//         padding: 12,
//         displayColors: false
//       },
//       legend: {
//         position: 'top' as const
//       }
//     },
//     scales: {
//       x: {
//         type: 'category',
//         offset: true,
//         ticks: {
//           padding: 10
//         },
//         grid: {
//           drawTicks: false
//         },
//         title: {
//           display: true,
//           text: 'Days'
//         }
//       },
//       y: {
//         title: {
//           display: true,
//           text: 'Size (mm)'
//         },
//         beginAtZero: true,
//         min: 0,
//         max: 30,
//         ticks: {
//           stepSize: 2
//         }
//       }
//     }
//   };

//   return (
//     <div className={Styles.hormonal}>
//       <Scatter data={scatterData} options={options} />
//     </div>
//   );
// };

// export default FollicularGraph;
